import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "./Reveal";
import { PORTFOLIO, PORTFOLIO_FILTERS } from "../data/siteContent";
import { stopScroll, startScroll } from "../hooks/useLenis";

const ORIENTATIONS = {
  tall: "aspect-[3/4]",
  wide: "aspect-[4/3]",
  square: "aspect-square",
};

export default function Portfolio() {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const items = filter === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === filter);

  useEffect(() => {
    if (lightbox === null) return;
    stopScroll();
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % items.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      startScroll();
      window.removeEventListener("keydown", onKey);
    };
  }, [lightbox === null, items.length]);

  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-20 sm:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Portfolio"
          title={
            <>
              Moments we <span className="italic text-gold">refused to miss</span>
            </>
          }
          sub="A peek into recent weddings across Keshod, Junagadh, Rajkot, Somnath, Diu and beyond."
        />

        <Reveal className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10 sm:mb-12">
          {PORTFOLIO_FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`portfolio-filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => {
                setFilter(f);
                setLightbox(null);
              }}
              className={`text-[12px] uppercase tracking-[0.16em] px-5 py-2.5 border transition-all duration-300 ${
                filter === f
                  ? "bg-gold text-ink border-gold font-semibold"
                  : "border-white/15 text-ivory-dim hover:border-gold/60 hover:text-gold"
              }`}
            >
              {f}
            </button>
          ))}
        </Reveal>

        <motion.div layout className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          <AnimatePresence mode="popLayout">
            {items.map((item, i) => (
              <motion.figure
                layout
                key={item.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.6, delay: Math.min(i * 0.05, 0.4), ease: EASE }}
                className="relative mb-5 break-inside-avoid group cursor-pointer overflow-hidden"
                data-testid={`portfolio-item-${item.id}`}
                onClick={() => setLightbox(i)}
              >
                <img
                  src={item.src}
                  alt={item.title}
                  loading="lazy"
                  className={`w-full ${ORIENTATIONS[item.orientation]} object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <figcaption className="absolute inset-x-0 bottom-0 p-5 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-gold mb-1">
                    {item.category}
                  </p>
                  <p className="font-display text-lg text-ivory">{item.title}</p>
                </figcaption>
                <span className="absolute inset-3 border border-gold/0 group-hover:border-gold/40 pointer-events-none transition-all duration-500" />
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && items[lightbox] && (
          <motion.div
            data-testid="lightbox"
            className="fixed inset-0 z-[100] bg-ink/[0.96] backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
          >
            <button
              data-testid="lightbox-close"
              aria-label="Close"
              className="absolute top-5 right-5 p-2.5 border border-white/20 text-ivory hover:border-gold hover:text-gold transition-colors"
              onClick={() => setLightbox(null)}
            >
              <X className="w-5 h-5" />
            </button>
            <button
              data-testid="lightbox-prev"
              aria-label="Previous photo"
              className="absolute left-3 sm:left-8 p-3 border border-white/20 text-ivory hover:border-gold hover:text-gold transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((i) => (i - 1 + items.length) % items.length);
              }}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <motion.button
              data-testid="lightbox-next"
              aria-label="Next photo"
              className="absolute right-3 sm:right-8 p-3 border border-white/20 text-ivory hover:border-gold hover:text-gold transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((i) => (i + 1) % items.length);
              }}
            >
              <ChevronRight className="w-5 h-5" />
            </motion.button>

            <motion.div
              key={items[lightbox].id}
              className="max-w-4xl w-full"
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={items[lightbox].src}
                alt={items[lightbox].title}
                className="w-full max-h-[78vh] object-contain"
              />
              <div className="flex items-end justify-between mt-4 px-1">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-gold mb-1">
                    {items[lightbox].category}
                  </p>
                  <p className="font-display text-xl text-ivory">{items[lightbox].title}</p>
                </div>
                <p className="text-xs text-ivory-mute tracking-[0.2em]">
                  {lightbox + 1} / {items.length}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
